import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Pill, Plus, Calendar } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Medications = ({ baby }) => {
  const navigate = useNavigate();
  const [medications, setMedications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [formData, setFormData] = useState({
    medication_name: '',
    dosage: '',
    frequency: '',
    prescribed_date: '',
    end_date: '',
    notes: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchMedications();
  }, [baby]);

  const fetchMedications = async () => {
    try {
      const response = await axios.get(`${API}/medications/${baby.id}`);
      setMedications(response.data);
    } catch (error) {
      console.error('Error fetching medications:', error);
      toast.error('Failed to load medications');
    } finally {
      setLoading(false);
    }
  };

  const handleAddMedication = async (e) => {
    e.preventDefault();
    
    if (!formData.medication_name || !formData.dosage || !formData.frequency || !formData.prescribed_date) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      const medicationData = {
        baby_id: baby.id,
        medication_name: formData.medication_name,
        dosage: formData.dosage,
        frequency: formData.frequency,
        prescribed_date: formData.prescribed_date,
        end_date: formData.end_date || null,
        notes: formData.notes || null
      };
      
      await axios.post(`${API}/medications`, medicationData);
      toast.success('Medication added successfully!');
      setShowAddDialog(false);
      setFormData({
        medication_name: '',
        dosage: '',
        frequency: '',
        prescribed_date: '',
        end_date: '',
        notes: ''
      });
      fetchMedications();
    } catch (error) {
      console.error('Error adding medication:', error);
      toast.error('Failed to add medication');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    // If already in DD/MM/YYYY format, return as is
    if (dateString && dateString.includes('/')) {
      return dateString;
    }
    // Otherwise convert from ISO format
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const isActiveMedication = (medication) => {
    if (!medication.end_date) return true;
    return new Date(medication.end_date) >= new Date();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading medications...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="dashboard-header">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate('/')}
              className="text-white hover:bg-white hover:bg-opacity-20"
              data-testid="back-button"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
                <Pill className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold" style={{fontFamily: 'Space Grotesk'}}>Medications</h1>
                <p className="text-blue-200 text-sm">{baby.name}</p>
              </div>
            </div>
          </div>
          
          <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
            <DialogTrigger asChild>
              <Button 
                className="bg-white bg-opacity-20 hover:bg-opacity-30 text-white"
                data-testid="add-medication-button"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Medication
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Add Medication</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleAddMedication} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="medication_name">Medication Name *</Label>
                  <Input
                    id="medication_name"
                    placeholder="Enter medication name"
                    value={formData.medication_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, medication_name: e.target.value }))}
                    data-testid="medication-name-input"
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="dosage">Dosage *</Label>
                  <Input
                    id="dosage"
                    placeholder="e.g., 2.5ml, 5mg"
                    value={formData.dosage}
                    onChange={(e) => setFormData(prev => ({ ...prev, dosage: e.target.value }))}
                    data-testid="medication-dosage-input"
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="frequency">Frequency *</Label>
                  <Input
                    id="frequency"
                    placeholder="e.g., Twice daily, Every 8 hours"
                    value={formData.frequency}
                    onChange={(e) => setFormData(prev => ({ ...prev, frequency: e.target.value }))}
                    data-testid="medication-frequency-input"
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="prescribed_date">Prescribed Date *</Label>
                  <Input
                    id="prescribed_date"
                    type="date"
                    value={formData.prescribed_date}
                    onChange={(e) => setFormData(prev => ({ ...prev, prescribed_date: e.target.value }))}
                    max={new Date().toISOString().split('T')[0]}
                    data-testid="medication-prescribed-date-input"
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="end_date">End Date (Optional)</Label>
                  <Input
                    id="end_date"
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => setFormData(prev => ({ ...prev, end_date: e.target.value }))}
                    data-testid="medication-end-date-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="notes">Notes (Optional)</Label>
                  <Textarea
                    id="notes"
                    placeholder="Additional notes about the medication..."
                    value={formData.notes}
                    onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                    rows={3}
                    data-testid="medication-notes-input"
                  />
                </div>
                
                <div className="flex space-x-2 pt-4">
                  <Button 
                    type="submit" 
                    className="flex-1"
                    disabled={submitting}
                    data-testid="save-medication-button"
                  >
                    {submitting ? 'Saving...' : 'Save Medication'}
                  </Button>
                  <Button 
                    type="button"
                    variant="outline" 
                    onClick={() => setShowAddDialog(false)}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="px-6 py-6">
        {medications.length === 0 ? (
          <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 text-center" data-testid="no-medications">
            <Pill className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Medications</h3>
            <p className="text-gray-600 mb-4">No medications recorded for {baby.name}.</p>
            <Button onClick={() => setShowAddDialog(true)} data-testid="add-first-medication-button">
              <Plus className="w-4 h-4 mr-2" />
              Add First Medication
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {medications.map((medication) => (
              <div key={medication.id} className="bg-white rounded-xl p-6 shadow-sm border border-gray-100" data-testid={`medication-${medication.id}`}>
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-start space-x-3">
                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <Pill className="w-6 h-6 text-green-600" />
                    </div>
                    
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className="text-lg font-semibold text-gray-900" style={{fontFamily: 'Space Grotesk'}}>
                          {medication.medication_name}
                        </h3>
                        {isActiveMedication(medication) && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            Active
                          </span>
                        )}
                      </div>
                      
                      <div className="space-y-1 text-sm text-gray-600">
                        <div><strong>Dosage:</strong> {medication.dosage}</div>
                        <div><strong>Frequency:</strong> {medication.frequency}</div>
                        <div className="flex items-center space-x-2">
                          <Calendar className="w-4 h-4" />
                          <span>Prescribed: {formatDate(medication.prescribed_date)}</span>
                        </div>
                        {medication.end_date && (
                          <div>End Date: {formatDate(medication.end_date)}</div>
                        )}
                        {medication.notes && (
                          <div className="mt-2 pt-2 border-t border-gray-100">
                            <strong>Notes:</strong> {medication.notes}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Medications;